import { useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type { Layout } from "react-grid-layout";

import {
  layoutToPinLayouts,
  pinsToLayout,
} from "src/analytics/dashboard/dashboardGridLayout";
import { sortPinsByPosition } from "src/analytics/dashboard/dashboardLayout";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import {
  dashboardKeys,
  defaultDashboardId,
} from "src/api/analytics-dashboard/dashboard";
import { useCurrentWorkspace } from "src/api/queries";
import type { Realm } from "src/clients/analytics-dashboard";
import { toastActions } from "src/design-system/Toast/utils";
import { isPreconditionError } from "src/utils/predicates";
import { toastError } from "src/utils/toastError";

const SAVE_ERROR_TITLE = "Couldn't save layout";

// No `response` means the request never reached the server (offline, DNS,
// timeout) as opposed to the server responding with an error status.
const isConnectivityError = (error: unknown): boolean =>
  isAxiosError(error) && !error.response;

export type DashboardLayoutEditing = {
  isEditing: boolean;
  canEdit: boolean;
  isSaving: boolean;
  layout: Layout;
  onLayoutChange: (layout: Layout) => void;
  start: () => void;
  cancel: () => void;
  save: () => void;
};

export const useDashboardLayoutEditing = (
  realm: Realm,
  realmId: string,
  dashboardId?: string,
): DashboardLayoutEditing => {
  const { dashboard, updatePinLayouts, isUpdating } = useDashboard(
    realm,
    realmId,
    dashboardId,
  );
  const { data: workspace } = useCurrentWorkspace();
  const wsId = workspace?.id ?? "";
  const queryClient = useQueryClient();
  const pins = useMemo(
    () => sortPinsByPosition(Object.values(dashboard.pins)),
    [dashboard.pins],
  );
  const [isEditing, setIsEditing] = useState(false);
  const [_dirtyLayout, setLayout] = useState<Layout>([]);

  // Drop pins unpinned mid-edit.
  const reconciledLayout = useMemo(() => {
    const pinIds = new Set(pins.map((pin) => pin.id));
    return _dirtyLayout.filter((item) => pinIds.has(item.i));
  }, [_dirtyLayout, pins]);

  // The route reuses this component instance across flows (and the dashboard is
  // cached per workspace), so reset edit state when the dashboard identity
  // changes or it would leak between dashboards/workspaces.
  const dashboardKey = `${wsId}:${realm}:${realmId}:${dashboardId ?? "default"}`;
  const [activeKey, setActiveKey] = useState(dashboardKey);
  if (activeKey !== dashboardKey) {
    setActiveKey(dashboardKey);
    setIsEditing(false);
    setLayout([]);
  }

  return {
    isEditing,
    canEdit: pins.length > 0,
    isSaving: isUpdating,
    layout: reconciledLayout,
    onLayoutChange: setLayout,
    start: () => {
      setLayout(pinsToLayout(pins));
      setIsEditing(true);
    },
    // Discard the edit, including any optimistic layout a failed save left in
    // the cache, so the read-only grid snaps back to the server state.
    cancel: () => {
      setIsEditing(false);
      const resolvedId = dashboardId ?? defaultDashboardId(realm, realmId);
      void queryClient.invalidateQueries({
        queryKey: dashboardKeys.dashboard(wsId, realm, realmId, resolvedId),
      });
    },
    // Stay in edit mode until the write settles (the button shows progress).
    // On failure keep edit mode open — the user's edits live in `_dirtyLayout`,
    // so they can retry — and surface the error.
    save: () => {
      updatePinLayouts(layoutToPinLayouts(reconciledLayout))
        .then(() => setIsEditing(false))
        .catch((error: unknown) => {
          if (isPreconditionError(error)) {
            toastActions.failure({
              title: SAVE_ERROR_TITLE,
              description:
                "This dashboard changed elsewhere while you were editing. Refresh the page and try again.",
            });
            return;
          }
          if (isConnectivityError(error)) {
            toastActions.failure({
              title: SAVE_ERROR_TITLE,
              description:
                "The server is not responding. Check your connection and try again.",
            });
            return;
          }
          toastError(error, { title: SAVE_ERROR_TITLE });
        });
    },
  };
};
